/**
 * Folder Dialog Component
 *
 * Modal dialog for creating and editing folders.
 * Handles denomination inheritance settings.
 */

import React, { useState, useEffect } from 'react';
import { useFolderStore } from '../stores/folderStore';
import { useAuthStore } from '../stores/authStore';
import type { CreateFolderParams, UpdateFolderParams } from '../api/folders';
import { DENOMINATIONS as BASE_DENOMINATIONS, DOCUMENT_TYPES } from '../constants/denominations';

const DENOMINATIONS = [
  { value: '', label: 'None (no inheritance)' },
  ...BASE_DENOMINATIONS,
];

export const FolderDialog: React.FC = () => {
  const { user } = useAuthStore();
  const {
    isCreateDialogOpen,
    isEditDialogOpen,
    selectedFolder,
    isSaving,
    error,
    createNewFolder,
    updateSelectedFolder,
    loadFolderFilesCount,
    closeCreateDialog,
    closeEditDialog,
    openWarningDialog,
  } = useFolderStore();

  const isOpen = isCreateDialogOpen || isEditDialogOpen;
  const isEditMode = isEditDialogOpen && selectedFolder !== null;

  const [formData, setFormData] = useState<CreateFolderParams>({
    folder_path: '',
    denomination_id: '',
    document_type: '',
    is_official: false,
    description: '',
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isCreateDialogOpen) {
      setFormData({
        folder_path: '',
        denomination_id: '',
        document_type: '',
        is_official: false,
        description: '',
      });
    } else if (isEditDialogOpen && selectedFolder) {
      setFormData({
        folder_path: selectedFolder.folder_path,
        denomination_id: selectedFolder.denomination_id || '',
        document_type: selectedFolder.document_type || '',
        is_official: selectedFolder.is_official,
        description: selectedFolder.description || '',
      });
    }
  }, [isCreateDialogOpen, isEditDialogOpen, selectedFolder]);

  const handleClose = () => {
    if (isCreateDialogOpen) {
      closeCreateDialog();
    } else {
      closeEditDialog();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isCreateDialogOpen) {
      // Create new folder
      const params: CreateFolderParams = {
        folder_path: formData.folder_path,
        // Only superusers can set denomination_id and is_official
        denomination_id: user?.is_superuser ? (formData.denomination_id || undefined) : undefined,
        document_type: formData.document_type || undefined,
        is_official: user?.is_superuser ? formData.is_official : false,
        description: formData.description || undefined,
      };
      await createNewFolder(params);
    } else if (isEditDialogOpen && selectedFolder) {
      // Check if denomination is changing (only relevant for superusers)
      const denominationChanging = user?.is_superuser &&
        formData.denomination_id !== (selectedFolder.denomination_id || '');

      if (denominationChanging) {
        // Load file count to determine if we need to warn
        await loadFolderFilesCount(selectedFolder.id);
        // Close edit dialog and open warning dialog
        closeEditDialog();
        openWarningDialog();
      } else {
        // Update folder (no warning needed)
        const params: UpdateFolderParams = {
          // Only superusers can set denomination_id and is_official
          denomination_id: user?.is_superuser ? (formData.denomination_id || undefined) : undefined,
          document_type: formData.document_type || undefined,
          is_official: user?.is_superuser ? formData.is_official : false,
          description: formData.description || undefined,
        };
        await updateSelectedFolder(params, false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-dark-card rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-border">
          <h2 className="text-xl font-semibold text-white">
            {isEditMode ? 'Edit Folder' : 'Create New Folder'}
          </h2>
          <button
            onClick={handleClose}
            className="text-dark-text-muted hover:text-white transition-colors"
            disabled={isSaving}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Folder Path */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Folder Path *
            </label>
            <input
              type="text"
              value={formData.folder_path}
              onChange={(e) => setFormData({ ...formData, folder_path: e.target.value })}
              placeholder={user?.is_superuser ? "/Presbyterian/Creeds" : "/Sermons/2024"}
              className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-dark-text-muted focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-colors"
              required
              disabled={isEditMode || isSaving}
            />
            <p className="text-xs text-dark-text-muted mt-1">
              {user?.is_superuser
                ? "Hierarchical path like /Presbyterian/Creeds"
                : "Organize your documents, e.g., /Sermons/2024"
              }
            </p>
          </div>

          {/* Denomination ID - Only visible to superusers */}
          {user?.is_superuser && (
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Denomination ID
              </label>
              <select
                value={formData.denomination_id}
                onChange={(e) => setFormData({ ...formData, denomination_id: e.target.value })}
                className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-white focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-colors"
                disabled={isSaving}
              >
                {DENOMINATIONS.map(denom => (
                  <option key={denom.value} value={denom.value} className="bg-dark-card text-white">
                    {denom.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-dark-text-muted mt-1">
                Files in this folder will inherit this denomination
              </p>
            </div>
          )}

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Document Type
            </label>
            <select
              value={formData.document_type}
              onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
              className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-white focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-colors"
              disabled={isSaving}
            >
              {DOCUMENT_TYPES.map(docType => (
                <option key={docType.value} value={docType.value} className="bg-dark-card text-white">
                  {docType.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-dark-text-muted mt-1">
              Default document type for files in this folder
            </p>
          </div>

          {/* Is Official - Only visible to superusers */}
          {user?.is_superuser && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_official"
                checked={formData.is_official}
                onChange={(e) => setFormData({ ...formData, is_official: e.target.checked })}
                className="w-4 h-4 text-accent-blue bg-dark-bg border-dark-border rounded focus:ring-2 focus:ring-accent-blue focus:ring-offset-0"
                disabled={isSaving}
              />
              <label htmlFor="is_official" className="text-sm font-medium text-white">
                Official documents folder
              </label>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={user?.is_superuser ? "Presbyterian creeds and confessions" : "My sermon notes and outlines"}
              rows={3}
              className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-dark-text-muted focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-colors"
              disabled={isSaving}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-white bg-dark-bg hover:bg-bridge-hover border border-dark-border rounded-lg transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-white bg-gradient-to-r from-accent-blue to-accent-orange rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FolderDialog;
